import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Star, Shield, Clock, Truck, CheckCircle, MapPin, Users, DollarSign, MessageCircle, Calculator, ShieldCheck, Globe, Package, Home, Building2, Briefcase, Sofa } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MobileCTA } from '../components/MobileCTA';
import { businessConfig, trackPhoneClick } from '../config/business';

const iconMap = {
  Users, Shield, MapPin, DollarSign, MessageCircle, Calculator, Truck, ShieldCheck, Globe,
  Home, Building2, Briefcase, Sofa, Package, Clock, CheckCircle
};

const HomePage = () => {
  const { company, contact, reviews, insurance, pricing, services, serviceAreas, differentiators, howItWorks, trustPoints, assets } = businessConfig;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Melbourne Removalists From $130/hr | Professional Moving | Thrifty Move</title>
        <meta name="description" content={`Professional Melbourne removalists from $130/hour including 2 removalists and GST. ${reviews.displayText}. Family-owned, fully insured. Get your free quote.`} />
        <link rel="canonical" href="https://thrifty-move.com.au/" />
        <meta property="og:title" content="Melbourne Removalists From $130/hr | Thrifty Move" />
        <meta property="og:description" content={`Professional Melbourne removalists from $130/hour. ${reviews.displayText}. Two-hour minimum. Additional call-out fee applies.`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MovingCompany",
            "name": company.name,
            "description": company.description,
            "telephone": contact.phone,
            "email": contact.email,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1230b North Road",
              "addressLocality": "Oakleigh South",
              "addressRegion": "VIC",
              "postalCode": "3167",
              "addressCountry": "AU"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": reviews.rating.toString(),
              "reviewCount": reviews.count.toString()
            }
          })}
        </script>
      </Helmet>
      
      <Header />

      {/* ============ HERO SECTION ============ */}
      <section className="relative bg-gray-900 pt-28 md:pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, #fbbf24 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-yellow-500"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            
            {/* Google Review Badge */}
            <div className="inline-flex items-center bg-white px-4 py-2 rounded-full mb-6 shadow-lg">
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4 mr-2" />
              <div className="flex text-yellow-500 mr-2">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
              </div>
              <span className="text-gray-900 font-semibold text-sm">{reviews.displayText}</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Professional Melbourne Removalists
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 mb-6 max-w-2xl mx-auto">
              Reliable, affordable moving services with two professional removalists included.
            </p>

            {/* PROMINENT PRICING DISPLAY */}
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-2xl p-6 md:p-8 mb-8 shadow-2xl max-w-md mx-auto">
              <div className="text-gray-900">
                <div className="text-sm font-semibold uppercase tracking-wider mb-1">From</div>
                <div className="text-5xl md:text-6xl font-bold mb-2">$130<span className="text-2xl md:text-3xl">/hr</span></div>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm font-medium">
                  <span>GST Included</span>
                  <span>•</span>
                  <span>2 Removalists</span>
                  <span>•</span>
                  <span>2-Hour Min</span>
                </div>
              </div>
            </div>

            {/* Primary CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Link to="/quote">
                <Button className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-lg px-8 py-6 transition-all duration-200 hover:scale-105 shadow-xl">
                  <ArrowRight className="mr-2 w-5 h-5" />
                  Get a Free Quote
                </Button>
              </Link>
              <a 
                href={contact.phoneLink}
                onClick={() => trackPhoneClick('Hero - Call')}
                data-phone-number="true"
              >
                <Button className="w-full sm:w-auto bg-white hover:bg-gray-100 text-gray-900 font-bold text-lg px-8 py-6 transition-all duration-200 hover:scale-105 shadow-xl">
                  <Phone className="mr-2 w-5 h-5" />
                  <span data-phone-number="true">{contact.phone}</span>
                </Button>
              </a>
            </div>

            {/* Pricing Disclaimer */}
            <p className="text-sm text-gray-400 mb-6">
              Additional travel/call-out fee applies based on location.
            </p>

            {/* Trust Points */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-400">
              <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> Fully Insured</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> Family Owned</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> Clear Pricing</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST BAR ============ */}
      <section className="bg-yellow-500 py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10 text-gray-900">
            {trustPoints.map((point, i) => {
              const Icon = iconMap[point.icon] || CheckCircle;
              return (
                <div key={i} className="flex items-center gap-2">
                  <Icon className="w-5 h-5" />
                  <span className="font-semibold text-sm md:text-base">{point.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ SERVICES OVERVIEW ============ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Moving Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional moving solutions for homes and businesses across Melbourne
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
            {services.slice(0, 6).map((service) => {
              const Icon = iconMap[service.icon] || Package;
              return (
                <Card key={service.id} className="border-2 border-gray-100 hover:border-yellow-400 transition-all duration-200 hover:shadow-lg">
                  <CardContent className="p-5 md:p-6 text-center">
                    <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1 text-sm md:text-base">{service.title}</h3>
                    <p className="text-gray-500 text-xs md:text-sm">{service.shortDescription}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-semibold px-8 py-3">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ WHY THRIFTY MOVE ============ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Thrifty Move
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A straightforward approach to moving: clear communication, transparent pricing, and careful handling.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {differentiators.map((item, i) => {
              const Icon = iconMap[item.icon] || CheckCircle;
              return (
                <Card key={i} className="border-0 shadow-md hover:shadow-lg transition-shadow bg-white">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-gray-900" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Three simple steps from quote to moving day
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {howItWorks.map((step, i) => (
                <div key={i} className="text-center relative">
                  <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-gray-900">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                  {i < 2 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-yellow-300"></div>
                  )}
                </div>
              ))}
            </div>

            <p className="text-center text-gray-500 text-sm mt-8 max-w-2xl mx-auto">
              Submitting your quote is a request, not an automatic booking. Our team reviews every quote and confirms availability before your move is scheduled.
            </p>
          </div>
        </div>
      </section>

      {/* ============ PRICING PREVIEW ============ */}
      <section className="py-16 md:py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Transparent Pricing
              </h2>
              <p className="text-gray-400">
                Volume-based pricing tiers. Your rate depends on your estimated move size.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {pricing.tiers.map((tier, i) => (
                <Card key={i} className={`border-2 ${i === 1 ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 bg-white'}`}>
                  <CardContent className="p-6 text-center">
                    <div className="text-sm font-bold text-gray-900 mb-1">{tier.label}</div>
                    <div className="text-xs font-medium text-gray-500 mb-2">{tier.volumeRange}</div>
                    <div className="text-4xl font-bold text-gray-900 mb-1">
                      {tier.hourlyRateDisplay ? (
                        <>{tier.hourlyRateDisplay}<span className="text-lg font-normal text-gray-500">/hr</span></>
                      ) : (
                        <>${tier.hourlyRate}<span className="text-lg font-normal text-gray-500">/hr</span></>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">{tier.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-gray-800 rounded-xl p-6 mb-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-white">
                {pricing.includes.map((item, i) => (
                  <div key={i} className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-gray-700 text-sm text-gray-400">
                <span>{pricing.modifiers.sundaySurcharge}</span>
              </div>
            </div>

            <div className="text-center">
              <p className="text-gray-400 text-sm mb-6">{pricing.calloutNote}</p>
              <Link to="/pricing">
                <Button className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-8 py-4">
                  View Full Pricing Details
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SOCIAL PROOF ============ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-8 h-8 text-yellow-500 fill-current" />
                ))}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {reviews.displayText}
              </h2>
              <p className="text-gray-600">Real reviews from real Melbourne moves</p>
            </div>

            {/* Featured Review */}
            <Card className="border-2 border-yellow-400 shadow-lg mb-8">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-gray-900">A</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-lg text-gray-900">Aya</span>
                      <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded">Verified</span>
                    </div>
                    <div className="flex text-yellow-500">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                    </div>
                  </div>
                </div>
                <blockquote className="text-gray-700 leading-relaxed">
                  <p className="font-semibold text-gray-900 mb-3 border-l-4 border-yellow-500 pl-4">
                    "The Thrifty Move team made my very difficult and stressful move so much easier!"
                  </p>
                  <p className="mb-2">From the initial inquiry and quote to the booking process, their communication was incredibly fast, professional, and reliable. On moving day, the team started exactly on time and worked very efficiently.</p>
                  <p className="font-medium text-gray-900">Their pricing is also very reasonable. I would 100% recommend them to anyone planning a move.</p>
                </blockquote>
              </CardContent>
            </Card>

            <div className="text-center">
              <Link to="/reviews">
                <Button variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-semibold px-8 py-3">
                  Read All {reviews.count}+ Reviews
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ RIGHT CAPACITY ============ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  The Right Capacity for Your Move
                </h2>
                <p className="text-gray-600 mb-4">
                  Every move is different. Our online quote system helps you estimate your move volume so we can match you with the appropriate truck capacity and crew.
                </p>
                <p className="text-gray-600 mb-6">
                  Whether you're moving a studio apartment or a large family home, we'll ensure you have the right resources for a smooth move.
                </p>
                <Link to="/quote">
                  <Button className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-6 py-3">
                    <Calculator className="w-5 h-5 mr-2" />
                    Build Your Quote
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <img 
                  src={assets.truckPhoto}
                  alt="Thrifty Move truck loaded for a Melbourne move"
                  className="rounded-xl shadow-lg w-full"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-lg p-4">
                  <p className="text-gray-900 font-semibold text-sm">Carefully loaded and secured for transport</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICE AREAS ============ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-yellow-500 px-4 py-2 rounded-full mb-4">
              <MapPin className="w-5 h-5 text-gray-900" />
              <span className="text-gray-900 font-bold">Melbourne Removalists</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Serving All Melbourne
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {serviceAreas.description}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {serviceAreas.regions.filter(r => r.highlight).map((region, i) => (
              <div key={i} className="mb-6 bg-yellow-50 p-5 rounded-xl border border-yellow-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-yellow-600" />
                  {region.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {region.suburbs.map(suburb => (
                    <span key={suburb} className="bg-white text-gray-700 px-3 py-1 rounded-full text-sm border border-yellow-200">
                      {suburb}
                    </span>
                  ))}
                </div>
              </div>
            ))}

            <p className="text-center text-gray-500 text-sm mb-6">Plus all other Melbourne suburbs</p>

            <div className="text-center">
              <Link to="/areas">
                <Button variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-semibold px-8 py-3">
                  View All Service Areas
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ PREVIEW ============ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Common Questions
            </h2>
            
            <div className="space-y-4">
              {[
                { q: 'Are you insured?', a: `Yes, Thrifty Move carries ${insurance.publicLiability} public liability insurance and ${insurance.goodsInTransit} goods in transit coverage.` },
                { q: 'How do I get a quote?', a: 'Use our online quote system to build your inventory and get an estimated price. Our team will review and confirm the details.' },
                { q: 'What areas do you service?', a: 'We service all Melbourne suburbs, with strong coverage across South-East and Eastern Melbourne.' }
              ].map((faq, i) => (
                <Card key={i} className="border border-gray-200">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                    <p className="text-gray-600 text-sm">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/policies">
                <Button variant="link" className="text-gray-900 font-semibold">
                  View All FAQs & Policies →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-16 md:py-20 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Move?
          </h2>
          <p className="text-xl text-gray-800 mb-8 max-w-2xl mx-auto">
            Get your free quote in minutes or call us to discuss your move.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white font-bold text-lg px-10 py-6 shadow-xl">
                <ArrowRight className="mr-2 w-5 h-5" />
                Get a Free Quote
              </Button>
            </Link>
            <a 
              href={contact.phoneLink}
              onClick={() => trackPhoneClick('Final CTA - Call')}
            >
              <Button className="w-full sm:w-auto bg-white hover:bg-gray-100 text-gray-900 font-bold text-lg px-10 py-6 shadow-xl">
                <Phone className="mr-2 w-5 h-5" />
                {contact.phone}
              </Button>
            </a>
          </div>

          <div className="mt-6">
            <a 
              href={contact.whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center text-gray-800 hover:text-gray-900 font-semibold"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Or message us on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
};

export default HomePage;
